import { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { ADMIN_CONNECTORS_QUERY_KEY } from '../api/categories'
import { useForbiddenGuard, useWriteForbiddenGuard } from '../api/guard'
import { titledErrorMessage } from '../api/problems'
import {
  MIN_SELECTED_TOOLS,
  putToolSelection,
  toolSelectionKey,
  useToolSelection,
  type ToolSelectionResult,
} from '../api/toolSelection'
import { ConfirmDialog } from './ConfirmDialog'
import '../views/screens.css'

interface ToolSelectionEditorProps {
  connectorName: string
  label: string
}

// What re-ingest does with the stored selection (gateway/registry/tool_selection.py,
// openapi.yaml setConnectorToolSelection).
export const REINGEST_NOTE =
  'The selection is stored and re-applied on every re-ingest (spec re-upload, promote): ' +
  'deselected tools stay off, and operations that are new in a re-uploaded spec stay ' +
  'inactive until you select them.'

/**
 * Tool-selection checklist for one OpenAPI-ingested connector (rbac-restore R4 / D54).
 * GET populates the checklist from each tool's `is_active`; Save sends ONE PUT with
 * exactly the checked names (spec order). At least one tool must be checked: the server
 * requires `min_length=1` and this editor never sends `null` (clear curation).
 */
export function ToolSelectionEditor({ connectorName, label }: ToolSelectionEditorProps) {
  const qc = useQueryClient()
  const query = useToolSelection(connectorName)
  useForbiddenGuard(query.error)

  const tools = query.data?.tools ?? []
  const active = tools.filter((t) => t.is_active).map((t) => t.name)
  const [selected, setSelected] = useState<string[] | null>(null)
  const chosen = selected ?? active
  const chosenSet = new Set(chosen)
  // Spec order, as the server stores the allowlist.
  const body = tools.filter((t) => chosenSet.has(t.name)).map((t) => t.name)
  const [confirming, setConfirming] = useState(false)
  const [okMsg, setOkMsg] = useState<string | null>(null)

  const save = useMutation({
    mutationFn: (names: string[]) => putToolSelection(connectorName, names),
    onSuccess: (data: ToolSelectionResult) => {
      // The PUT answers with the full selection: use it, no refetch of this panel.
      qc.setQueryData(toolSelectionKey(connectorName), data)
      setSelected(null)
      setOkMsg(
        data.changed
          ? `Saved: ${data.active_tool_count} of ${data.tool_count} tools active ` +
              `(${data.activated.length} turned on, ${data.deactivated.length} turned off).`
          : `Nothing changed: ${data.active_tool_count} of ${data.tool_count} tools active.`,
      )
      // The connector list shows active/total tool counts.
      qc.invalidateQueries({ queryKey: ADMIN_CONNECTORS_QUERY_KEY })
    },
    onError: () => setOkMsg(null),
  })
  useWriteForbiddenGuard(save.error)

  const setChosen = (next: string[]) => {
    setOkMsg(null)
    setSelected(next)
  }
  const toggle = (name: string) =>
    setChosen(chosenSet.has(name) ? chosen.filter((n) => n !== name) : [...chosen, name])

  const empty = body.length < MIN_SELECTED_TOOLS
  const dirty = body.length !== active.length || body.some((n) => !active.includes(n))
  const turningOff = active.filter((n) => !chosenSet.has(n)).length
  const turningOn = body.filter((n) => !active.includes(n)).length

  return (
    <div className="scr-exposure" role="group" aria-label={`Tool selection for ${label}`}>
      {query.isLoading && <p className="scr-muted">Loading tools…</p>}
      {query.isError && (query.error as { status?: number }).status !== 403 && (
        <p className="scr-err" role="alert">
          {titledErrorMessage(query.error)}
        </p>
      )}

      {query.isSuccess && (
        <>
          <p className="scr-note">
            {query.data.curated ? 'Curated' : 'Not curated (every tool exposed)'} ·{' '}
            {query.data.active_tool_count} of {query.data.tool_count} tools active.
          </p>
          <fieldset className="scr-fieldset">
            <legend>
              Exposed tools ({body.length} selected; at least {MIN_SELECTED_TOOLS})
            </legend>
            <div className="scr-actions">
              <button
                type="button"
                className="screen-btn"
                disabled={save.isPending}
                onClick={() => setChosen(tools.map((t) => t.name))}
              >
                Select all
              </button>
              <button
                type="button"
                className="screen-btn"
                disabled={save.isPending}
                onClick={() => setChosen([])}
              >
                Select none
              </button>
            </div>
            <div className="scr-tool-list">
              {tools.map((t) => (
                <label key={t.name} className="scr-check scr-mono">
                  <input
                    type="checkbox"
                    checked={chosenSet.has(t.name)}
                    disabled={save.isPending}
                    onChange={() => toggle(t.name)}
                  />
                  {t.name}
                </label>
              ))}
              {tools.length === 0 && <span className="scr-muted">No tools in the current spec.</span>}
            </div>
          </fieldset>

          {empty && tools.length > 0 && (
            <p className="scr-err" role="status">
              Select at least one tool. To expose no tools, take the connector dark instead.
            </p>
          )}

          <div className="scr-actions">
            <button
              type="button"
              className="screen-btn screen-btn-primary"
              disabled={empty || !dirty || save.isPending}
              onClick={() => setConfirming(true)}
            >
              {save.isPending ? 'Saving…' : 'Save tool selection'}
            </button>
            {okMsg && <span className="scr-ok">{okMsg}</span>}
          </div>
          <p className="scr-note">{REINGEST_NOTE}</p>
        </>
      )}

      {save.isError && (
        <p className="scr-err" role="alert">
          {titledErrorMessage(save.error)}
        </p>
      )}

      {confirming && (
        <ConfirmDialog
          label="Confirm tool selection"
          confirmDisabled={empty}
          onCancel={() => setConfirming(false)}
          onConfirm={() => {
            setConfirming(false)
            if (body.length < MIN_SELECTED_TOOLS) return
            setOkMsg(null)
            save.mutate(body)
          }}
        >
          <p>
            <strong>{label}</strong>: expose exactly {body.length} of {tools.length} tools (
            {turningOn} on, {turningOff} off)? Deselected tools disappear from every tenant on
            the next call.
          </p>
        </ConfirmDialog>
      )}
    </div>
  )
}
